import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Card,
    CardContent,
    CardActions,
    Typography,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Link,
    Alert,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonOutline from '@mui/icons-material/PersonOutline';
import LockOutlined from '@mui/icons-material/LockOutlined';

import { login as loginApi, getCurrentUser } from '../../api';
import { useAuth } from './AuthContext';
import { clearStoredTokens, storeTokens } from '../../utils/tokenStorage';
import { isKeycloakMode } from '../../auth/keycloakClient';

export default function Login() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [remember, setRemember] = useState(true);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});

    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const didTriggerKeycloakLogin = React.useRef(false);

    useEffect(() => {
        if (isKeycloakMode()) {
            const params = new URLSearchParams(location.search);
            const reason = params.get('reason');
            // React.StrictMode in dev double-invokes effects; ensure we only redirect once.
            // If we're coming from a previous failure, don't auto-loop.
            if (reason) {
                if (reason === 'expired') {
                    setError('Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.');
                } else if (reason === 'forbidden') {
                    setError('Zugriff verweigert. Bitte melde dich erneut an.');
                } else if (reason === 'keycloak_error') {
                    setError('Keycloak-Login ist fehlgeschlagen. Bitte erneut versuchen.');
                }
            } else if (!didTriggerKeycloakLogin.current) {
                didTriggerKeycloakLogin.current = true;
                login(null);
            }
            return;
        }
        const params = new URLSearchParams(location.search);
        const reason = params.get('reason');
        if (reason === 'forbidden') {
            setError('Zugriff verweigert. Melde dich mit einem Konto an, das berechtigt ist.');
        } else if (reason === 'expired') {
            setError('Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.');
        }
    }, [location.search, login]);

    if (isKeycloakMode()) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    background:
                        'linear-gradient(135deg, rgba(255,165,0,0.10) 0%, rgba(255,165,0,0.04) 40%, rgba(0,0,0,0.02) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    py: { xs: 6, md: 8 },
                }}
            >
                <Container maxWidth="sm">
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h3" fontWeight={800} gutterBottom>
                            Anmeldung
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            Du wirst über <strong>Keycloak</strong> angemeldet
                        </Typography>
                    </Box>

                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
                            border: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden',
                        }}
                    >
                        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            <Typography variant="body1" color="text.secondary">
                                Falls du nicht automatisch weitergeleitet wurdest, klicke hier:
                            </Typography>
                        </CardContent>
                        <CardActions sx={{ px: { xs: 3, md: 4 }, pb: { xs: 3, md: 4 } }}>
                            <Button
                                onClick={() => {
                                    didTriggerKeycloakLogin.current = true;
                                    setError('');
                                    login(null);
                                }}
                                variant="contained"
                                sx={{
                                    backgroundColor: 'orange',
                                    '&:hover': { backgroundColor: '#cc7a00' },
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.2,
                                    fontWeight: 600,
                                }}
                            >
                                Mit Keycloak anmelden
                            </Button>
                        </CardActions>
                    </Card>
                </Container>
            </Box>
        );
    }

    const clearFieldError = (field) => {
        setFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});

        const clientErrors = {};
        if (!username.trim()) {
            clientErrors.username = 'Bitte Benutzername oder E-Mail eingeben.';
        }
        if (!password) {
            clientErrors.password = 'Bitte Passwort eingeben.';
        }
        if (Object.keys(clientErrors).length > 0) {
            setFieldErrors(clientErrors);
            setError('Bitte prüfe die markierten Felder.');
            return;
        }

        try {
            const res = await loginApi({ username, password });

            if (res.status === 200) {
                const tokens = res.data;

                if (!tokens || typeof tokens !== 'object') {
                    throw new Error('Unerwartete Antwort vom Server.');
                }

                const { accessToken, refreshToken } = tokens;

                if (!accessToken) {
                    throw new Error('Anmeldeantwort enthielt kein Zugriffstoken.');
                }

                storeTokens({ accessToken, refreshToken, remember });

                try {
                    const me = await getCurrentUser();
                    login(me.data, { remember });
                    navigate('/loginSuccess');
                } catch (fetchErr) {
                    clearStoredTokens();
                    setError('Benutzerdaten konnten nicht geladen werden. Bitte erneut anmelden.');
                }
            } else {
                setError('Login fehlgeschlagen.');
            }
        } catch (err) {
            // Falls Backend JSON liefert wie {"error":"token_expired","message":"JWT expired"}
            const backendError = err?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;

            if (err?.response?.status === 401) {
                // Token rauswerfen, egal wo er gespeichert war
                clearStoredTokens();

                if (backendError?.error === 'token_expired') {
                    setError('Deine Sitzung ist abgelaufen. Bitte erneut einloggen.');
                } else {
                    setFieldErrors({
                        username: 'Benutzername oder E-Mail prüfen.',
                        password: 'Passwort prüfen.',
                    });
                    setError('Ungültige Anmeldedaten. Bitte erneut versuchen.');
                }
            } else if (details && details.length > 0) {
                const nextErrors = {};
                details.forEach((detail) => {
                    const rawField = detail?.field;
                    const message = detail?.message;
                    if (!rawField || !message) return;
                    const field = rawField.split('.').pop();
                    nextErrors[field] = message;
                });
                if (Object.keys(nextErrors).length > 0) {
                    setFieldErrors(nextErrors);
                    setError(backendError?.message || 'Bitte prüfe die markierten Felder.');
                    return;
                }
            } else {
                const msg =
                    typeof backendError === 'string'
                        ? backendError
                        : err?.message || 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.';
                setError(msg);
            }
        }
    };


    return (
        <Box
            sx={{
                minHeight: '100vh',
                background:
                    'linear-gradient(135deg, rgba(255,165,0,0.10) 0%, rgba(255,165,0,0.04) 40%, rgba(0,0,0,0.02) 100%)',
                display: 'flex',
                alignItems: 'center',
                py: { xs: 6, md: 8 },
            }}
        >
            <Container maxWidth="sm">
                {/* Hero-Kopf */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h3" fontWeight={800} gutterBottom>
                        Willkommen zurück
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        Melde dich bei <strong>Schreiberlein</strong> an
                    </Typography>
                </Box>

                {/* Login-Card */}
                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 4,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Box component="form" onSubmit={handleLogin} noValidate>
                            <TextField
                                label="Benutzername oder E-Mail"
                                value={username}
                                onChange={(e) => {
                                    setUsername(e.target.value);
                                    clearFieldError('username');
                                    setError('');
                                }}
                                autoComplete="username"
                                fullWidth
                                required
                                margin="normal"
                                error={Boolean(fieldErrors.username)}
                                helperText={fieldErrors.username || ''}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonOutline fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <TextField
                                label="Passwort"
                                type={showPw ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    clearFieldError('password');
                                    setError('');
                                }}
                                autoComplete="current-password"
                                fullWidth
                                required
                                margin="normal"
                                error={Boolean(fieldErrors.password)}
                                helperText={fieldErrors.password || ''}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockOutlined fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="Passwort anzeigen"
                                                onClick={() => setShowPw((s) => !s)}
                                                edge="end"
                                            >
                                                {showPw ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Box
                                sx={{
                                    mt: 1,
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={remember}
                                            onChange={(e) => setRemember(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label="Angemeldet bleiben"
                                />

                                <Link
                                    component={RouterLink}
                                    to="/forgot-password"
                                    underline="hover"
                                    color="inherit"
                                    sx={{ fontSize: 14 }}
                                >
                                    Passwort vergessen?
                                </Link>
                            </Box>

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{
                                    mt: 1.5,
                                    py: 1.2,
                                    fontWeight: 700,
                                    borderRadius: 2,
                                    backgroundColor: 'orange',
                                    '&:hover': { backgroundColor: '#cc7a00' },
                                }}
                            >
                                Einloggen
                            </Button>
                        </Box>
                    </CardContent>

                    <CardActions
                        sx={{
                            px: { xs: 3, md: 4 },
                            py: 2,
                            justifyContent: 'space-between',
                            bgcolor: 'rgba(255,165,0,0.06)',
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Neu bei Schreiberlein?
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/registration"
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                borderColor: 'orange',
                                color: 'orange',
                                '&:hover': { borderColor: '#cc7a00', color: '#cc7a00' },
                            }}
                        >
                            Konto erstellen
                        </Button>
                    </CardActions>
                </Card>

                {/* Kleiner Footer-Hinweis */}
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                        Durch die Anmeldung stimmst du unseren{' '}
                        <Link component={RouterLink} to="/terms" underline="hover">
                            AGB
                        </Link>{' '}
                        &{' '}
                        <Link component={RouterLink} to="/privacy" underline="hover">
                            Datenschutz
                        </Link>{' '}
                        zu.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}
